import React, { Context, createContext, Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { fetchStores } from './util/api';
import { useSearchParams } from 'react-router-dom';
import { Store } from './types';
import Component from './components/templates';

const defaultLocation = { country: '', region: '', city: '' };

interface FeatureFilter {
    code: string;
    value: string | null;
}

interface LocationFilters {
    country: string;
    region: string;
    city: string;
}

interface StoreState {
    stores: Store[];
    allStores: Store[];
    locationFilters: LocationFilters;
    setLocationFilters: Dispatch<SetStateAction<LocationFilters>>;
    featureFilters: FeatureFilter[];
    setFeatureFilters: Dispatch<SetStateAction<FeatureFilter[]>>;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    filterStock: boolean,
    setFilterStock: Dispatch<SetStateAction<boolean>>;
    article: string | null;
}

export const StoresContext: Context<StoreState> = createContext({
    stores: [],
    allStores: [],
    locationFilters: defaultLocation,
    setLocationFilters: () => {},
    featureFilters: [],
    setFeatureFilters: () => {},
    search: '',
    setSearch: () => {},
    filterStock: false,
    setFilterStock: () => {},
    article: null,
} as StoreState);

function App() {
    const [stores, setStores] = useState<Store[]>([]);
    const [locationFilters, setLocationFilters] = useState(defaultLocation);
    const [featureFilters, setFeatureFilters] = useState<FeatureFilter[]>([]);
    const [search, setSearch] = useState('');
    const [params] = useSearchParams();
    const article = useMemo(() => params.get('article'), [params]);
    const [filterStock, setFilterStock] = useState(!!article);
    const filteredStores = [];
    outer: for (const s of stores) {
        if(filterStock && !s.product?.stock){
            continue;
        }
        for (const [key, value] of Object.entries(locationFilters)) {
            if (value && s[key as keyof typeof locationFilters] !== value) {
                continue outer;
            }
        }
        for (const { code, value } of featureFilters) {
            if (!s.featuresByCode[code] || s.featuresByCode[code].value !== value) {
                continue outer;
            }
        }
        if (search && !s.searchable.includes(search)) {
            continue;
        }
        filteredStores.push(s);
    }
    useEffect(() => {
        fetchStores(article).then(setStores);
    }, [article]);
    return (
        <StoresContext.Provider
            value={{
                stores: filteredStores,
                allStores: stores,
                locationFilters,
                setLocationFilters,
                featureFilters,
                setFeatureFilters,
                search,
                setSearch,
                filterStock,
                setFilterStock,
                article,
            }}
        >
            <Component />
        </StoresContext.Provider>
    );
}

export default App;
